import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import axios from 'axios';
import {
  PRODUCT_CATEGORIES_INITIAL_STATE,
  ProductCategoriesActionParams,
  ProductCategoriesState
} from '@modules/product-categories/store/product-categories.model.ts';
import {
  createProductCategories,
  deleteProductCategories,
  editProductCategories
} from '@api/product-categories-api.ts';
import { getAllUnits } from '@api/departments-and-units/units.api.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useProductCategoriesStore = create<ProductCategoriesState>()(
  devtools(
    (set, getState) => ({
      ...PRODUCT_CATEGORIES_INITIAL_STATE,
      ...withCallState(getState),

      getDepartmentUnit: async () => {
        try {
          const unitsData = await getAllUnits();

          const data = unitsData.map((unit) => ({
            id: unit.id,
            title: unit.name,
            value: unit.name,
            type: unit.type
          }));

          set({
            departmentUnit: data
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createProductCategories: async ({
        data,
        state,
        handleClose
      }: ProductCategoriesActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const res = await createProductCategories(data as any);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          if (state) state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_product_category_text',
            SnackbarType.Success
          );
          return res;
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }

          return null;
        }
      },

      editProductCategories: async ({
        data,
        id,
        state,
        handleClose
      }: ProductCategoriesActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editProductCategories(data as any, id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_update_product_category_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteProductCategories: async ({
        id,
        state,
        handleClose
      }: ProductCategoriesActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await deleteProductCategories(id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_delete_product_category_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      }
    }),
    { name: 'ProductCategoriesStore' }
  )
);

export default useProductCategoriesStore;
